import React, { useEffect, useState } from 'react';
import { Button, Col, ProgressBar, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { IParams } from '../../../../../model/shared/IParams';
import { IBookingStats } from '../../../../../model/campaign/Campaign';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { getCorrectImageThumbnail, isDateInPast } from '../../../../../shared/functions/Functions';
import { CampaignDashboardService } from '../service';
import { ErrorToast } from '../../../../../utils/toasters';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { IconBook } from '../../../../../shared/Icons';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { CampaignChildDashCard } from './CampaignChildDashCard';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import { DashStatsCardSkeleton } from './CampaignDashboardSkeletons';

interface ICampaignBookingCardProps {
    isCharity?: boolean;
}

export function CampaignBookingCard({ isCharity }: ICampaignBookingCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();
    const { isChildCampaign, campaign } = useSelector((states: IAllStates) => states.selectedCampaign);
    const [booking, setBooking] = useState<IBookingStats>();
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        CampaignDashboardService.getBookingData(id)
            .then(data => {
                setBooking(data);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsLoading(false));
    }, [id]);

    const linkToBook = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_BOOK}/${id}`);
    };
    if (isLoading) {
        return <DashStatsCardSkeleton stats={4} withSubtitle withButton withImages withProgress={!isCharity} />;
    }

    return (
        <CardLayout classCard="mb-3">
            <div className="d-flex justify-content-between align-items-top">
                <div className="d-flex cursor-pointer textHover" onClick={linkToBook}>
                    <div>
                        <IconBook width="20" height="20" color={MUTED_TEXT_COLOR} classes="me-2" />
                    </div>
                    <div className="d-flex flex-column">
                        <h6 className="mb-0 ">{t('brand.campaign.dashboard.booking.book')}</h6>
                        <span className="text-muted">{t('brand.campaign.dashboard.booking.bookDesc')}</span>
                    </div>
                </div>
                <div className="ms-2">
                    <Button onClick={linkToBook} className="my-3" variant="outline-primary" size="sm">
                        {t('brand.campaign.dashboard.booking.view_offers')}
                    </Button>
                </div>
            </div>
            {isChildCampaign && <CampaignChildDashCard stats={booking} />}
            <Row className="align-items-center my-3">
                <Col className="d-flex flex-wrap">
                    <div className="col-6 col-lg px-0">
                        <h3 className="text-primary">{priceSplitter(booking?.totalMatchedInfluencers ?? 0)}</h3>
                        <div>{t('brand.campaign.dashboard.booking.total_matched_influencers')}</div>
                    </div>
                    <div className="col-6 col-lg px-0">
                        <h3 className="text-primary">{booking?.offersReceived}</h3>
                        <div>{t('brand.campaign.dashboard.booking.offers_received')}</div>
                    </div>
                    <div className="col-6 col-lg px-0">
                        <h3 className="text-primary">{booking?.influencersBooked}</h3>
                        <div>{t('brand.campaign.dashboard.booking.influencers_booked')}</div>
                    </div>
                    <div className="col-6 col-lg px-0">
                        <h3 className="text-primary">{booking?.offersDeclined}</h3>
                        <div>{t('brand.campaign.dashboard.booking.offers_declined')}</div>
                    </div>
                </Col>
                <Col md={'auto'} className="d-none d-md-flex justify-content-end">
                    {!!booking?.feedItems?.length ? (
                        booking?.feedItems?.map(book => {
                            return (
                                <React.Fragment key={book?.id}>
                                    {book?.postImages?.map(image => {
                                        return (
                                            <CustomImageHandler
                                                key={image?.id}
                                                thumbnailPath={image?.thumbnail}
                                                altTag={image?.name}
                                                classes="dashboard-sm-image ml-2"
                                                placeholder={getCorrectImageThumbnail(
                                                    !!image?.externalLink,
                                                    image?.name ?? '',
                                                    image?.thumbPath ?? '',
                                                    image?.path
                                                )}
                                                photoPath={getCorrectImageThumbnail(
                                                    !!image?.externalLink,
                                                    image?.name ?? '',
                                                    image?.thumbPath ?? '',
                                                    image?.path
                                                )}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <EmptyState
                            desc={''}
                            imgClass="w-40px"
                            iconPath="/icons/emptyBrandCampaignBook.png"
                            btnText={`Influencer ${t('general.button.search')}`}
                            navigateTo={`/brand/campaign/book/${id}/invite`}
                            tooltipDesc={t(`brand.campaign.book.invite.filters.not_approved_campaign`)}
                            disableButton={
                                campaign?.approvalStatus === 'pending' || isDateInPast(campaign?.publishTo ?? '')
                            }
                            addClass="text-center"
                        />
                    )}
                </Col>
            </Row>
            {isCharity ? (
                <div />
            ) : (
                <>
                    <div className="d-flex justify-content-between">
                        <div>
                            {isChildCampaign
                                ? t('brand.campaign.dashboard.booking.additional_booking_usage')
                                : t('brand.campaign.dashboard.booking.budget_usage')}
                        </div>
                        <div>
                            <span className="text-primary">{priceSplitter(booking?.budget.totalSpent as number)}</span>{' '}
                            / {priceSplitter(booking?.budget.grossBudget as number)}
                        </div>
                        <div className="word-break-word">{booking?.budget.budgetPercentage}%</div>
                    </div>
                    <ProgressBar
                        variant="primary"
                        className="progress-sm w-100 bg-primary-lighter"
                        now={booking?.budget.budgetPercentage}
                    />
                </>
            )}
        </CardLayout>
    );
}
